
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';
import { Form } from '@/components/ui/form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  customerFormSchema, 
  ahavexerFormSchema, 
  FormData, 
  CustomerFormData,
  AhavexerFormData 
} from './forms/RegisterFormSchema';
import { RegisterFormFields } from './forms/RegisterFormFields';
import { RegisterFormSubmitButton } from './forms/RegisterFormSubmitButton';

interface RegisterFormProps {
  userType: 'ahavexer' | 'customer';
  onSuccess?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ userType, onSuccess }) => {
  const { registerMember, registerCustomer } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const formSchema = userType === 'ahavexer' ? ahavexerFormSchema : customerFormSchema;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: userType === 'ahavexer' 
      ? {
          email: '',
          password: '',
          confirmPassword: '',
          fullName: '',
          ahavexId: '',
          address: '',
        }
      : {
          email: '',
          password: '',
          confirmPassword: '',
        },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      if (userType === 'ahavexer') {
        const ahavexerData = data as AhavexerFormData;
        await registerMember(
          ahavexerData.email, 
          ahavexerData.password, 
          {
            fullName: ahavexerData.fullName,
            ahavexId: ahavexerData.ahavexId,
            address: ahavexerData.address
          }
        );
      } else {
        const customerData = data as CustomerFormData;
        await registerCustomer(customerData.email, customerData.password);
      }
      
      toast.success(`Successfully registered as ${userType}`);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast.error('Registration failed. Please try again with a different email.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-h-[60vh] overflow-y-auto px-4 pb-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <RegisterFormFields form={form} userType={userType} />
          <RegisterFormSubmitButton isLoading={isLoading} />
        </form>
      </Form>
    </div>
  );
};

export default RegisterForm;
